// Minimal SigV4 URL signer for AWS IoT WebSocket connections.
// Builds:
//   wss://<endpoint>/mqtt?X-Amz-Algorithm=AWS4-HMAC-SHA256&...&X-Amz-Signature=<sig>
//
// Hand-rolled instead of @smithy/signature-v4 because (a) it keeps the deck-side
// plugin bundle small, (b) URL signing is a ~80-line algorithm, (c) we sign once
// per connection, not per message — perf doesn't matter.

const enc = new TextEncoder();

async function sha256Hex(s) {
  const buf = await crypto.subtle.digest('SHA-256', typeof s === 'string' ? enc.encode(s) : s);
  return [...new Uint8Array(buf)].map(b => b.toString(16).padStart(2, '0')).join('');
}

async function hmac(key, data) {
  const k = await crypto.subtle.importKey('raw', typeof key === 'string' ? enc.encode(key) : key,
    { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
  return new Uint8Array(await crypto.subtle.sign('HMAC', k, enc.encode(data)));
}

async function deriveSigningKey(secretKey, dateStamp, region, service) {
  const kDate    = await hmac('AWS4' + secretKey, dateStamp);
  const kRegion  = await hmac(kDate,  region);
  const kService = await hmac(kRegion, service);
  return await hmac(kService, 'aws4_request');
}

function amzDate() {
  // ISO8601 basic format: 20260425T203015Z
  const d = new Date();
  const pad = (n, w = 2) => String(n).padStart(w, '0');
  return `${d.getUTCFullYear()}${pad(d.getUTCMonth()+1)}${pad(d.getUTCDate())}` +
         `T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`;
}

// Build a presigned WSS URL for the AWS IoT data endpoint.
// `creds` = { accessKeyId, secretAccessKey, sessionToken } from Cognito.
export async function presignIotWsUrl({ endpoint, region, creds, expiresSec = 300 }) {
  const service = 'iotdevicegateway';
  const algorithm = 'AWS4-HMAC-SHA256';

  const date = amzDate();             // 20260425T203015Z
  const dateStamp = date.slice(0, 8); // 20260425
  const credentialScope = `${dateStamp}/${region}/${service}/aws4_request`;

  // Canonical query (sorted alphabetically). Note: X-Amz-Security-Token is excluded
  // from the canonical query and appended AFTER signing — AWS IoT-specific quirk.
  const params = new URLSearchParams();
  params.set('X-Amz-Algorithm',     algorithm);
  params.set('X-Amz-Credential',    `${creds.accessKeyId}/${credentialScope}`);
  params.set('X-Amz-Date',          date);
  params.set('X-Amz-Expires',       String(expiresSec));
  params.set('X-Amz-SignedHeaders', 'host');
  // Sort keys.
  const sortedQuery = [...params.entries()].sort(([a],[b]) => a.localeCompare(b))
    .map(([k,v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join('&');

  const canonicalRequest = [
    'GET',
    '/mqtt',
    sortedQuery,
    `host:${endpoint}`,
    '',
    'host',
    await sha256Hex(''),
  ].join('\n');

  const stringToSign = [
    algorithm,
    date,
    credentialScope,
    await sha256Hex(canonicalRequest),
  ].join('\n');

  const signingKey = await deriveSigningKey(creds.secretAccessKey, dateStamp, region, service);
  const sigBuf     = await hmac(signingKey, stringToSign);
  const signature  = [...sigBuf].map(b => b.toString(16).padStart(2, '0')).join('');

  let url = `wss://${endpoint}/mqtt?${sortedQuery}&X-Amz-Signature=${signature}`;
  if (creds.sessionToken) {
    url += `&X-Amz-Security-Token=${encodeURIComponent(creds.sessionToken)}`;
  }
  return url;
}
